import { deepmerge } from '@mui/utils';
import type { ThemeOptions } from '@mui/material/styles';
import { plPL } from '@mui/material/locale';
import { defaultLightTheme, type RaThemeOptions } from 'react-admin';

/**
 * Motyw aplikacji: jasny motyw react-admina plus kolor Typography ze ścieżki
 * palety (`color="text.secondary"`).
 *
 * ## Po co (01.10.2026, przed prezentacją 02.10)
 *
 * W MUI do wersji 7 `color` na Typography był propem systemowym: wartość
 * spoza listy MUI szła do `sx` i była szukana w palecie, więc
 * `color="text.secondary"` dawał szary. MUI 9 propy systemowe zdjęło —
 * Typography zna już tylko klucze palety (`error`, `primary`…) oraz
 * `textPrimary` / `textSecondary` / `textDisabled`
 * (`@mui/material/Typography/Typography.js`, warianty `palette[color].main`
 * i `` `text${capitalize(color)}` ``). Dla „text.secondary” żaden wariant nie
 * pasuje, a prop nie trafia ani do stylu, ani do DOM: tekst dziedziczy kolor
 * rodzica i tekst pomocniczy w ok. 150 miejscach wychodził czarny. Typy tego
 * nie łapią, bo `color` przyjmuje dowolny napis.
 *
 * Zamiast przepisywać kilkadziesiąt plików, motyw przywraca to jedno
 * zachowanie: `color` w postaci `grupa.kanał`, który w palecie wskazuje
 * kolor, daje ten kolor. Wartości bez kropki zostają po stronie MUI, ścieżka
 * spoza palety nie daje żadnego koloru (jak dziś), a `sx` wygrywa z `color`
 * — style motywu idą przed `sx`, tak jak propy systemowe w MUI 7.
 *
 * `Box` ma ten sam problem (`color` idzie do DOM jako atrybut), ale nie ma
 * klucza w motywie — u nas nie występuje; tam kolor tylko przez `sx`.
 */

/**
 * Kolor z palety pod ścieżką `grupa.kanał` — `text.secondary`, `error.main`,
 * `grey.500`. Gdy ścieżka nie prowadzi do koloru w palecie — `undefined`:
 * nie zgadujemy i nie przepuszczamy napisu do CSS.
 */
export function paletteColor(palette: object, color: unknown): string | undefined {
  if (typeof color !== 'string') return undefined;
  const parts = color.split('.');
  if (parts.length !== 2) return undefined;
  const [group, channel] = parts;
  const entry: unknown = (palette as Record<string, unknown>)[group];
  if (typeof entry !== 'object' || entry === null) return undefined;
  const value: unknown = (entry as Record<string, unknown>)[channel];
  return typeof value === 'string' ? value : undefined;
}

const typographyPaletteColor: ThemeOptions = {
  components: {
    MuiTypography: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          const color = paletteColor(theme.palette, ownerState.color);
          return color === undefined ? {} : { color };
        },
      },
    },
  },
};

/**
 * To, co `<Admin theme>` dostaje — opcje motywu, nie gotowy motyw:
 * react-admin sam woła na nich `createTheme` (ra-ui-materialui
 * `theme/ThemeProvider.js`). Baza to `defaultLightTheme`, którego `<Admin>`
 * używa, gdy motywu nie podano — poza kolorem Typography wygląd bez zmian.
 */
/**
 * Polskie teksty wbudowane w kontrolki MUI (Autocomplete: „Brak opcji”,
 * „Wyczyść”, „Otwórz”; Alert: „Zamknij”…) — bez tego pola wyboru mówiły
 * po angielsku (poprawa brzmienia 01.10.2026). Jawne propsy w komponentach
 * (np. etykiety paginacji react-admina) mają pierwszeństwo.
 */
const polishMui: ThemeOptions = { components: plPL.components };

export const appTheme: RaThemeOptions = deepmerge(
  deepmerge(defaultLightTheme, polishMui),
  typographyPaletteColor,
);
